import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageLayout from 'components/common/PageLayout';
import { DateRangeFilterBlur } from 'components/filters';
import useTokenValidation from 'hooks/useTokenValidation';
import { InfraestruturaDataProvider, useInfraestruturaData } from '../context/InfraestruturaDataContext';
import ColaboradorDashboard from '../components/ColaboradorDashboard';
import InfraOverviewDashboard from '../components/InfraOverviewDashboard';
import InfraestruturaFilterChips from '../components/InfraestruturaFilterChips';
import MaterialBaseInfraDashboard from '../components/MaterialBaseInfraDashboard';
import OcupacaoRedeDashboard from '../components/OcupacaoRedeDashboard';
import OrdemServicoDashboard from '../components/OrdemServicoDashboard';
import SinalCtoDashboard from '../components/SinalCtoDashboard';
import ValoresDashboard from '../components/ValoresDashboard';
import {
  ActivitySquare,
  ChevronDown,
  DollarSign,
  Layers3,
  PackageSearch,
  Radio,
  Sparkles,
  Users,
  Wrench,
  X,
} from 'lucide-react';

const MAIN_MODULES = [
  { id: 'operacao', label: 'Operação', icon: Layers3 },
  { id: 'ocupacao-rede', label: 'Ocupação de rede', icon: ActivitySquare },
  { id: 'sinal-cto', label: 'Sinal de CTO', icon: Radio },
  { id: 'material-base', label: 'Material base', icon: PackageSearch },
];

const OPERATION_VIEWS = [
  { id: 'visao-geral', nome: 'Visão geral', icon: Sparkles },
  { id: 'colaborador', nome: 'Colaborador', icon: Users },
  { id: 'ordem-servico', nome: 'Ordem de serviço', icon: Wrench },
  { id: 'valores', nome: 'Valores', icon: DollarSign },
];

const LEGACY_MODULE_TO_VIEW = {
  colaborador: 'colaborador',
  'ordem-servico': 'ordem-servico',
  valores: 'valores',
};

const OPERATION_VIEW_CONTENT = {
  'visao-geral': {
    title: 'Operação integrada'
  },
  colaborador: {
    title: 'Colaborador',
    subtitle: 'Leitura operacional de equipes, produtividade, horas médias e distribuição de trabalho.',
  },
  'ordem-servico': {
    title: 'Ordem de serviço',
    subtitle: 'Painel analítico de finalizações, rompimentos, referências, cidades e detalhamento operacional.',
  },
  valores: {
    title: 'Valores',
    subtitle: 'Visão financeira-operacional de custo total, composição por dimensão e evolução mensal.',
  },
};

const MODULE_CONTENT = {
  'ocupacao-rede': {
    title: 'Ocupação de Rede',
    subtitle: 'Acompanhamento de ocupação e capacidade da infraestrutura de rede.',
    cards: [
      { label: 'CTOs monitoradas', value: '0', info: 'Sem leitura externa' },
      { label: 'Capacidade média', value: '0%', info: 'Aguardando cálculo real' },
      { label: 'Pontos críticos', value: '0', info: 'Sem alarmes configurados' },
    ],
  },
  'sinal-cto': {
    title: 'Sinal de CTO',
    subtitle: 'Monitoramento técnico de qualidade de sinal e estabilidade por CTO.',
    cards: [
      { label: 'CTOs com sinal estável', value: '0', info: 'Sem telemetria conectada' },
      { label: 'Oscilações detectadas', value: '0', info: 'Sem eventos recebidos' },
      { label: 'Alertas críticos', value: '0', info: 'Sem engine de alerta' },
    ],
  },
  'material-base': {
    title: 'Material Base',
    subtitle: 'Saldos de produtos e almoxarifados vinculados à infraestrutura.',
    cards: [
      { label: 'Produtos', value: '0', info: 'Aguardando leitura' },
      { label: 'Almoxarifados', value: '0', info: 'Aguardando leitura' },
      { label: 'Saldo total', value: '0', info: 'Aguardando cálculo' },
    ],
  },
};

const isValidMainModule = (moduleId) => MAIN_MODULES.some((module) => module.id === moduleId);
const isValidOperationView = (viewId) => OPERATION_VIEWS.some((view) => view.id === viewId);

const normalizeNavigationState = (searchParams) => {
  const rawModule = searchParams.get('module');
  const rawView = searchParams.get('view');

  if (rawModule && LEGACY_MODULE_TO_VIEW[rawModule]) {
    return { module: 'operacao', view: LEGACY_MODULE_TO_VIEW[rawModule] };
  }

  if (rawModule && isValidMainModule(rawModule)) {
    return {
      module: rawModule,
      view: rawModule === 'operacao' && isValidOperationView(rawView) ? rawView : 'visao-geral',
    };
  }

  if (rawView && isValidOperationView(rawView)) {
    return { module: 'operacao', view: rawView };
  }

  return { module: 'operacao', view: 'visao-geral' };
};

const InfraDataStatus = () => {
  const {
    loading,
    processing,
    progress,
    errorMessage,
    lastUpdated,
    evolutionCacheState,
    evolutionLastUpdated,
    evolutionScope,
    evolutionRequestedScope,
    hasHistoryFilters,
    retry,
  } = useInfraestruturaData();

  return (
    <div className="rounded-[18px] border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <div className="flex flex-col gap-2 text-xs text-[color:var(--text-muted)] lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2.5">
          {loading ? <span>Carregando dados unificados da infraestrutura...</span> : null}
          {processing && progress ? <span>{`Processando período selecionado… ${progress.ready || 0}/${progress.total || 0} blocos prontos. Os números podem refletir o último snapshot pronto até o aquecimento finalizar.`}</span> : null}
          {errorMessage ? <span className="text-rose-500 dark:text-rose-300">{errorMessage}</span> : null}
          {errorMessage ? (
            <button
              type="button"
              onClick={retry}
              className="rounded-full border border-rose-300 px-2.5 py-1 font-semibold text-rose-600 transition hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30"
            >
              Tentar novamente
            </button>
          ) : null}
          {!loading && !processing && !errorMessage ? <span>Dados compartilhados prontos para cruzamento entre subabas.</span> : null}
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
            {`Histórico: ${
              hasHistoryFilters
                ? (evolutionScope === 'filtered'
                  ? 'filtrado'
                  : evolutionRequestedScope === 'filtered'
                    ? 'geral (fallback)'
                    : 'geral')
                : 'geral'
            }`}
          </span>
          <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
            {`Estado: ${evolutionCacheState || 'idle'}`}
          </span>
          {lastUpdated ? (
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
              {`Atualizado às ${lastUpdated.toLocaleTimeString('pt-BR')}`}
            </span>
          ) : null}
          {evolutionLastUpdated ? (
            <span className="rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1">
              {`Histórico em ${evolutionLastUpdated}`}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const InfraestruturaContent = ({
  abaAtiva,
  subAbaAtiva,
  setSubAbaAtiva,
  showDropdown,
  setShowDropdown,
  showMainSheet,
  setShowMainSheet,
  isMobile,
  handleMainTabSelect,
  onMaterialBaseReloadReady,
}) => {
  const { range, setRange, loading, processing } = useInfraestruturaData();

  const isUnifiedModule = abaAtiva === 'operacao';
  const tabAtiva = MAIN_MODULES.find((tab) => tab.id === abaAtiva) || MAIN_MODULES[0];
  const TabIcon = tabAtiva.icon;
  const currentViewContent = isUnifiedModule
    ? OPERATION_VIEW_CONTENT[subAbaAtiva] || OPERATION_VIEW_CONTENT['visao-geral']
    : MODULE_CONTENT[abaAtiva];

  const desktopSidebarColumnClassName = 'w-64 shrink-0 self-start';
  const desktopSidebarShellClassName =
    'sticky top-[calc(var(--header-shell-height)-16px)] self-start w-full bg-[color:var(--surface)]/96 border border-t-0 border-l-0 border-[color:var(--header-control-border-subtle)] rounded-r-[24px] rounded-l-none rounded-tr-none px-3 pt-2.5 pb-4 shadow-[0_14px_36px_rgba(2,6,23,0.16)]';
  const desktopSidebarHeaderClassName = 'mb-3 border-b border-[color:var(--header-control-border-subtle)] px-2 pb-2';
  const desktopSidebarListClassName = 'app-scroll-subtle flex max-h-[calc(100dvh-168px)] flex-col gap-1.5 overflow-y-auto pr-1';
  const desktopSidebarItemClassName =
    'flex min-h-[46px] items-center gap-3 rounded-[14px] border px-3 py-3 text-sm transition-[background-color,color,border-color,box-shadow] duration-200';
  const desktopSidebarLabelClassName = 'min-w-0 flex-1 truncate text-left';
  const desktopSidebarInactiveClassName =
    'border-transparent text-[color:var(--text-primary)]/82 hover:bg-[color:var(--surface-muted)]/38 hover:text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)]';
  const desktopSidebarActiveClassName =
    'bg-[color:var(--surface-muted)]/58 text-[color:var(--text-primary)] border-[color:var(--header-control-border-subtle)] shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_0_0_1px_rgba(168,85,247,0.14)]';
  const desktopSidebarIconInactiveClassName = 'text-[color:var(--text-muted)] opacity-70';
  const desktopSidebarIconActiveClassName = 'text-[color:var(--accent)] opacity-95';
  const desktopSubtabsShellClassName =
    'app-scroll-subtle overflow-x-auto rounded-[20px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)]/92 px-2 py-2 shadow-[0_12px_32px_rgba(2,6,23,0.12)]';
  const desktopSubtabsTrackClassName = 'flex min-w-full w-max items-center gap-1.5';
  const desktopSubtabButtonBaseClassName =
    'group relative flex min-h-[46px] shrink-0 items-center gap-2.5 rounded-[16px] border px-4 py-2.5 text-sm transition-[background-color,color,border-color,box-shadow,transform] duration-200 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40 focus:ring-offset-0';
  const desktopSubtabActiveClassName =
    'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/68 text-[color:var(--text-primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_0_0_1px_rgba(168,85,247,0.12)]';
  const desktopSubtabInactiveClassName =
    'border-transparent text-[color:var(--text-muted)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/42 hover:text-[color:var(--text-primary)]';
  const mobileSubtabsShellClassName =
    'bg-[color:var(--surface)]/92 rounded-[18px] border border-[color:var(--header-control-border-subtle)] px-3 py-2 shadow-[0_10px_24px_rgba(2,6,23,0.12)]';
  const mobileSubtabTriggerClassName =
    'w-full flex min-h-[48px] items-center justify-between gap-3 rounded-[14px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-4 py-3 text-left text-sm text-[color:var(--text-primary)] transition-[background-color,border-color,box-shadow] duration-200 hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40 focus:ring-offset-0';
  const mobileSubtabListClassName =
    'absolute z-[80] mt-2 w-full overflow-hidden rounded-[16px] border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] shadow-[0_18px_40px_rgba(2,6,23,0.24)]';
  const mobileSubtabItemBaseClassName =
    'w-full flex min-h-[48px] items-center gap-3 px-4 py-3 text-left text-sm transition-[background-color,color] duration-150';

  const renderDesktopSidebar = () => (
    <div className={desktopSidebarColumnClassName}>
      <aside className={desktopSidebarShellClassName}>
        <div className={desktopSidebarHeaderClassName}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]/55">
            Infraestrutura
          </p>
          <p className="mt-1 text-[13px] text-[color:var(--text-muted)]/82">Navegação da diretoria</p>
        </div>
        <div className={desktopSidebarListClassName}>
          {MAIN_MODULES.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = abaAtiva === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleMainTabSelect(tab.id)}
                className={`${desktopSidebarItemClassName} ${
                  isActive ? desktopSidebarActiveClassName : desktopSidebarInactiveClassName
                }`}
                aria-label={`Abrir módulo ${tab.label}`}
              >
                <IconComponent
                  className={`h-4 w-4 flex-shrink-0 transition-colors duration-200 ${
                    isActive ? desktopSidebarIconActiveClassName : desktopSidebarIconInactiveClassName
                  }`}
                />
                <span className={`${desktopSidebarLabelClassName} ${isActive ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium'}`}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </aside>
    </div>
  );

  const renderDesktopSubtabs = () => (
    <div className={desktopSubtabsShellClassName}>
      <div className={desktopSubtabsTrackClassName}>
        {OPERATION_VIEWS.map((aba) => {
          const IconComponent = aba.icon;
          const isActive = subAbaAtiva === aba.id;
          return (
            <button
              key={aba.id}
              type="button"
              onClick={() => setSubAbaAtiva(aba.id)}
              className={`${desktopSubtabButtonBaseClassName} ${
                isActive ? desktopSubtabActiveClassName : desktopSubtabInactiveClassName
              }`}
              aria-label={`Selecionar ${aba.nome}`}
            >
              <IconComponent
                className={`h-4 w-4 flex-shrink-0 transition-colors duration-200 ${
                  isActive ? 'text-[color:var(--accent)] opacity-95' : 'text-[color:var(--text-muted)] opacity-65 group-hover:opacity-85'
                }`}
              />
              <span className={`whitespace-nowrap ${isActive ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium'}`}>
                {aba.nome}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderMobileSubtabs = () => {
    const activeSubtab = OPERATION_VIEWS.find((view) => view.id === subAbaAtiva) || OPERATION_VIEWS[0];
    const ActiveIcon = activeSubtab.icon;

    return (
      <div className={mobileSubtabsShellClassName}>
        <div className="infra-dropdown-container relative">
          <button type="button" onClick={() => setShowDropdown((v) => !v)} className={mobileSubtabTriggerClassName}>
            <span className="min-w-0 flex items-center gap-3">
              <ActiveIcon className="h-4 w-4 flex-shrink-0 text-[color:var(--accent)] opacity-90" />
              <span className="truncate font-medium">{activeSubtab.nome}</span>
            </span>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-[color:var(--text-muted)] transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
            />
          </button>

          {showDropdown && (
            <div className={mobileSubtabListClassName}>
              {OPERATION_VIEWS.map((aba) => {
                const IconComponent = aba.icon;
                const isActive = subAbaAtiva === aba.id;
                return (
                  <button
                    key={aba.id}
                    type="button"
                    onClick={() => {
                      setSubAbaAtiva(aba.id);
                      setShowDropdown(false);
                    }}
                    className={`${mobileSubtabItemBaseClassName} ${
                      isActive
                        ? 'bg-[color:var(--surface-muted)]/90 text-[color:var(--text-primary)]'
                        : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]/70'
                    }`}
                  >
                    <IconComponent className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[color:var(--accent)] opacity-95' : 'text-[color:var(--text-muted)] opacity-75'}`} />
                    <span className={`min-w-0 flex-1 truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>{aba.nome}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const activeDashboard = (() => {
    if (!isUnifiedModule) {
      if (abaAtiva === 'ocupacao-rede') {
        return <OcupacaoRedeDashboard isMobile={isMobile} />;
      }

      if (abaAtiva === 'sinal-cto') {
        return <SinalCtoDashboard isMobile={isMobile} />;
      }

      if (abaAtiva === 'material-base') {
        return <MaterialBaseInfraDashboard onReloadReady={onMaterialBaseReloadReady} />;
      }

      return null;
    }

    if (subAbaAtiva === 'colaborador') {
      return <ColaboradorDashboard isMobile={isMobile} />;
    }

    if (subAbaAtiva === 'ordem-servico') {
      return <OrdemServicoDashboard isMobile={isMobile} />;
    }

    if (subAbaAtiva === 'valores') {
      return <ValoresDashboard isMobile={isMobile} />;
    }

    return <InfraOverviewDashboard onSelectView={setSubAbaAtiva} isMobile={isMobile} />;
  })();

  return (
    <div className="w-full overflow-x-hidden px-4 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-5 md:px-0 md:pr-6 md:pt-0 lg:pr-8">
      {isMobile && (
        <div className="infra-main-sheet-container mb-6">
          <div className="rounded-2xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] p-2 shadow-[0_10px_24px_rgba(2,6,23,0.12)]">
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setShowMainSheet(true)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/60 px-3 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40"
              >
                <Layers3 className="h-4 w-4 text-[color:var(--accent)]" />
                Módulos
              </button>

              <div className="min-w-0 flex-1 rounded-xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] px-3 py-2.5">
                <div className="flex min-h-[24px] items-center gap-2">
                  <TabIcon className="h-4 w-4 flex-shrink-0 text-[color:var(--accent)]" />
                  <span className="truncate text-sm font-semibold text-[color:var(--text-primary)]">{tabAtiva.label}</span>
                </div>
              </div>
            </div>
          </div>

          {showMainSheet && (
            <div className="fixed inset-0 z-[100] flex items-end justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => setShowMainSheet(false)} aria-hidden="true" />
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Selecionar módulo de infraestrutura"
                className="relative w-full max-w-md rounded-t-2xl border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface)] p-4 shadow-[0_-12px_40px_rgba(2,6,23,0.35)]"
              >
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-semibold text-[color:var(--text-primary)]">Navegação da diretoria</p>
                  <button
                    type="button"
                    onClick={() => setShowMainSheet(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[color:var(--header-control-border-subtle)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]/40"
                    aria-label="Fechar menu de módulos"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="app-scroll-subtle max-h-[60dvh] space-y-1 overflow-y-auto pr-1">
                  {MAIN_MODULES.map((tab) => {
                    const IconComponent = tab.icon;
                    const isActive = abaAtiva === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => handleMainTabSelect(tab.id)}
                        className={`flex min-h-[48px] w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                          isActive
                            ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]/80 text-[color:var(--text-primary)]'
                            : 'border-transparent text-[color:var(--text-primary)] hover:border-[color:var(--header-control-border-subtle)] hover:bg-[color:var(--surface-muted)]/70'
                        }`}
                      >
                        <IconComponent className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`} />
                        <span className={`min-w-0 flex-1 truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className={isMobile ? '' : 'flex items-start gap-6'}>
        {!isMobile && renderDesktopSidebar()}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {isUnifiedModule && (!isMobile ? renderDesktopSubtabs() : renderMobileSubtabs())}

          <div className="rounded-[28px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_14px_34px_rgba(2,6,23,0.10)] sm:p-5">
            <div className="flex flex-col gap-5 lg:gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--text-muted)]/75">
                  <span>Infraestrutura</span>
                  <span className="h-1 w-1 rounded-full bg-[color:var(--text-muted)]/40" />
                  <span>{tabAtiva.label}</span>
                  {isUnifiedModule ? (
                    <>
                      <span className="h-1 w-1 rounded-full bg-[color:var(--text-muted)]/40" />
                      <span>{OPERATION_VIEWS.find((view) => view.id === subAbaAtiva)?.nome || 'Visão geral'}</span>
                    </>
                  ) : null}
                </div>
                <h1 className="mt-3 text-2xl font-semibold text-[color:var(--text-primary)] sm:text-[2rem]">
                  {currentViewContent?.title}
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-[color:var(--text-muted)]">
                  {currentViewContent?.subtitle}
                </p>
              </div>

              {isUnifiedModule ? (
                <div className="flex w-full flex-col gap-2 lg:max-w-[360px] lg:self-start xl:w-auto xl:min-w-[280px] xl:items-end">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:w-full xl:justify-end">
                    <span className="text-sm font-semibold text-[color:var(--text-primary)]">Período</span>
                    <DateRangeFilterBlur
                      value={range}
                      loading={loading || processing}
                      onChange={setRange}
                      className="w-full sm:w-auto sm:min-w-[260px]"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {isUnifiedModule ? (
            <>
              <InfraestruturaFilterChips />
              <InfraDataStatus />
            </>
          ) : null}

          {activeDashboard || (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {currentViewContent.cards.map((card) => (
                  <div
                    key={card.label}
                    className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-4 shadow-[0_8px_22px_rgba(2,6,23,0.08)]"
                  >
                    <p className="text-xs uppercase tracking-[0.08em] text-[color:var(--text-muted)]">{card.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-[color:var(--text-primary)]">{card.value}</p>
                    <p className="mt-2 text-xs text-[color:var(--text-muted)]">{card.info}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-5 shadow-[0_10px_24px_rgba(2,6,23,0.08)]">
                <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-[color:var(--text-primary)]">Em preparação para integração</h2>
                <p className="mt-2 text-sm text-[color:var(--text-muted)]">
                  Esta área continua isolada da operação unificada e está pronta para receber fontes específicas de telemetria, inventário e monitoramento.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Infraestrutura = () => {
  useTokenValidation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [abaAtiva, setAbaAtiva] = useState('operacao');
  const [subAbaAtiva, setSubAbaAtiva] = useState('visao-geral');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMainSheet, setShowMainSheet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [materialBaseReload, setMaterialBaseReload] = useState(null);

  useEffect(() => {
    const normalized = normalizeNavigationState(searchParams);

    if (abaAtiva !== normalized.module) {
      setAbaAtiva(normalized.module);
    }

    if (subAbaAtiva !== normalized.view) {
      setSubAbaAtiva(normalized.view);
    }

    const currentModule = searchParams.get('module');
    const currentView = searchParams.get('view');
    const shouldSync = currentModule !== normalized.module
      || (normalized.module === 'operacao' ? currentView !== normalized.view : currentView !== null);

    if (shouldSync) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('module', normalized.module);
      if (normalized.module === 'operacao') {
        nextParams.set('view', normalized.view);
      } else {
        nextParams.delete('view');
      }
      setSearchParams(nextParams, { replace: true });
    }
  }, [abaAtiva, searchParams, setSearchParams, subAbaAtiva]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.infra-dropdown-container')) {
        setShowDropdown(false);
      }
      if (showMainSheet && !event.target.closest('.infra-main-sheet-container')) {
        setShowMainSheet(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, showMainSheet]);

  const syncSearch = (moduleId, viewId = 'visao-geral') => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('module', moduleId);
    if (moduleId === 'operacao') {
      nextParams.set('view', viewId);
    } else {
      nextParams.delete('view');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleMainTabSelect = (tabId) => {
    const nextView = tabId === 'operacao' && isValidOperationView(subAbaAtiva) ? subAbaAtiva : 'visao-geral';
    setAbaAtiva(tabId);
    setSubAbaAtiva(nextView);
    setShowMainSheet(false);
    syncSearch(tabId, nextView);
  };

  const handleSubtabSelect = (viewId) => {
    setSubAbaAtiva(viewId);
    setShowDropdown(false);
    syncSearch('operacao', viewId);
  };

  const handleMaterialBaseReloadReady = useCallback((reloadFn) => {
    setMaterialBaseReload(() => reloadFn || null);
  }, []);

  const handleHeaderRefresh = useCallback(async () => {
    if (abaAtiva === 'material-base' && materialBaseReload) {
      await materialBaseReload();
    }
  }, [abaAtiva, materialBaseReload]);

  return (
    <PageLayout customRefreshFunction={abaAtiva === 'material-base' ? handleHeaderRefresh : null}>
      <InfraestruturaDataProvider>
        <InfraestruturaContent
          abaAtiva={abaAtiva}
          subAbaAtiva={subAbaAtiva}
          setSubAbaAtiva={handleSubtabSelect}
          showDropdown={showDropdown}
          setShowDropdown={setShowDropdown}
          showMainSheet={showMainSheet}
          setShowMainSheet={setShowMainSheet}
          isMobile={isMobile}
          handleMainTabSelect={handleMainTabSelect}
          onMaterialBaseReloadReady={handleMaterialBaseReloadReady}
        />
      </InfraestruturaDataProvider>
    </PageLayout>
  );
};

export default Infraestrutura;
